import { supabase } from '../../../shared/lib/supabase';

// Define the Team interface based on your schema
export interface Team {
  id: string;
  name: string;
  logo: string | null;
  country: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  venue: string | null;
  city: string | null;
  date_established: string | null;
  website: string | null;
  social_media: string | null;
  created_by: string | null;
  organisation_id: string | null;
  data_source: string;
  short_name: string | null;
  // joined
  organisation_name?: string | null;
}

export interface TeamCreateInput {
  name: string;
  logo?: string | null;
  country: string;
  active?: boolean;
  venue?: string | null;
  city?: string | null;
  date_established?: string | null;
  website?: string | null;
  social_media?: string | null;
  created_by?: string | null;
  organisation_id?: string | null;
  data_source: string;
  short_name?: string | null;
}

export interface TeamUpdateInput {
  name?: string;
  logo?: string | null;
  country?: string;
  active?: boolean;
  venue?: string | null;
  city?: string | null;
  date_established?: string | null;
  website?: string | null;
  social_media?: string | null;
  created_by?: string | null;
  organisation_id?: string | null;
  data_source?: string;
  short_name?: string | null;
}

class TeamsService {
  async getAll(): Promise<Team[]> {
    try {
      console.log('🔍 Fetching teams...');
      const { data, error } = await supabase
        .from('teams')
        .select(`*, organisations(name)`) 
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching teams:', error);
        throw error;
      }

      const transformed = (data || []).map((t: any) => ({
        ...t,
        organisation_name: t.organisations?.name || null
      }));

      console.log('✅ Teams fetched successfully:', transformed?.length || 0);
      return transformed;
    } catch (error) {
      console.error('Error in teamsService.getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Team | null> {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select(`*, organisations(name)`) 
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching team:', error);
        throw error;
      }

      return data ? { ...data, organisation_name: (data as any).organisations?.name || null } as any : data;
    } catch (error) {
      console.error('Error in teamsService.getById:', error);
      throw error;
    }
  }

  async create(team: TeamCreateInput): Promise<Team> {
    try {
      console.log('🔍 Creating team:', team);
      const { data, error } = await supabase
        .from('teams')
        .insert([team])
        .select()
        .single();

      if (error) {
        console.error('Error creating team:', error);
        throw error;
      }

      console.log('✅ Team created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in teamsService.create:', error);
      throw error;
    }
  }

  async update(id: string, team: TeamUpdateInput): Promise<Team> {
    try {
      console.log('🔍 Updating team:', id, team);
      const { data, error } = await supabase
        .from('teams')
        .update(team)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating team:', error);
        throw error;
      }

      console.log('✅ Team updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in teamsService.update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting team:', id);
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting team:', error);
        throw error;
      }

      console.log('✅ Team deleted successfully');
    } catch (error) {
      console.error('Error in teamsService.delete:', error);
      throw error;
    }
  }
}

export const teamsService = new TeamsService();
